// modules
import { Box, Text } from 'ink';
import { useState } from 'react';
import { Form, Field } from 'react-final-form';

// components
import TextInput from './TextInput';
import ErrorDisplay from './ErrorDisplay';

// logic
import postToFirebaseFunction from '../utilities/postToFirebaseFunction';
import { signInWithCustomToken } from '../utilities/firestore';
import { setAuthConfig } from '../utilities/configStore';

const loginFields = [
  {
    name: 'email',
    label: 'Your email',
    placeholder: 'name@email.com',
    Input: TextInput,
    validate: (value: string) => {
      if (!value) return 'Required';
    },
  },
  {
    name: 'accessToken',
    label: 'Your access token',
    placeholder: '********',
    Input: TextInput,
    validate: (value: string) => {
      if (!value) return 'Required';
    },
    mask: '*',
  },
];

export default function Login({
  setIsLoggedIn,
}: {
  setIsLoggedIn: (value: boolean) => void;
}) {
  const [activeField, setActiveField] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const onFailure = (message: string) => {
    setIsSubmittingForm(false);
    setFailureMessage(message);
    setActiveField(0);
  };

  // login handler that verifies accessToken and email with that in firebase
  const onSubmitLogin = async ({
    email,
    accessToken,
  }: {
    email: string;
    accessToken: string;
  }) => {
    setFailureMessage(null);
    setIsSubmittingForm(true);

    try {
      const jwtToken = await postToFirebaseFunction('loginWithAccessToken', {
        email,
        accessToken,
      });

      if (jwtToken) {
        await signInWithCustomToken(jwtToken);
        setAuthConfig(email, accessToken, jwtToken);
        setIsLoggedIn(true);
      } else {
        onFailure('Incorrrect credentials. Try again');
      }
    } catch (err: any) {
      if (err.code === 500) {
        setError(err);
      } else {
        onFailure(
          `Login unsuccessful: ${err?.response?.data?.message || err.message}`,
        );
      }
    }
  };

  if (error) return <ErrorDisplay error={error} />;

  return (
    <>
      <Text>You are not currently logged in.</Text>
      <Text>Log in below:</Text>
      <Box marginBottom={1} />
      <Form onSubmit={onSubmitLogin}>
        {({ handleSubmit, validating }) => (
          <Box flexDirection="column">
            {loginFields.map(
              ({ name, label, placeholder, validate, Input, mask }, index) => (
                <Field name={name} key={name} validate={validate}>
                  {({ input, meta }) => (
                    <Box flexDirection="column">
                      {activeField >= index ? (
                        <Box>
                          <Text bold={activeField === index}>{label}: </Text>
                          {activeField === index ? (
                            <Input
                              {...input}
                              mask={mask}
                              placeholder={placeholder}
                              onSubmit={() => {
                                if (meta.valid && !validating) {
                                  setActiveField((value) => value + 1); // go to next field
                                  if (activeField === loginFields.length - 1) {
                                    // last field, so submit
                                    handleSubmit();
                                  }
                                } else {
                                  input.onBlur(); // mark as touched to show error
                                }
                              }}
                            />
                          ) : (
                            (input.value && (
                              <Text>
                                {name === 'accessToken'
                                  ? input.value.replace(/./gi, '*')
                                  : input.value}
                              </Text>
                            )) ||
                            (placeholder && (
                              <Text color="gray">{placeholder}</Text>
                            ))
                          )}
                          {meta.invalid && meta.touched && (
                            <Box marginLeft={2} marginRight={1}>
                              <Text color="red">✖</Text>
                            </Box>
                          )}
                          {meta.error && meta.touched && (
                            <LoginError errorMessage={meta.error} />
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Field>
              ),
            )}
          </Box>
        )}
      </Form>

      {isSubmittingForm && (
        <Box marginTop={1}>
          <Text>Logging in...</Text>
        </Box>
      )}

      {failureMessage && <LoginError errorMessage={failureMessage} marginTop />}
    </>
  );
}

function LoginError({
  errorMessage,
  marginTop,
}: {
  errorMessage: string;
  marginTop?: boolean;
}) {
  return (
    <Box marginTop={marginTop ? 1 : 0}>
      <Text color="red">{errorMessage}</Text>
    </Box>
  );
}
